import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Dialog, DialogContent, IconButton, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// Secours : si l'API ne répond pas, la section garde les avis d'origine.
import avisDeSecours from '../data/avis.json';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const ROUGE = '#c62828';
const ETOILE = '#f5b301';
const FOND = '#f7f8fc';
const DELAI_MOBILE = 6000;       // un avis toutes les 6 secondes
const PAUSE_APRES_ACTION = 10000; // on laisse lire après un clic ou un glissement

const SECOURS = {
  avis: avisDeSecours.avis.map((a, i) => ({ id: `secours-${i}`, ...a, date_avis: null, source: 'google' })),
  reglages: { note: null, nombre_avis: null, lien_google: avisDeSecours.lien_google, lien_avis: avisDeSecours.lien_avis },
};

// Même couleur d'initiale pour une même personne, d'un chargement à l'autre.
const COULEURS_AVATAR = ['#c62828', '#1565c0', '#2e7d32', '#ef6c00', '#6a1b9a', '#00838f'];
const couleurAvatar = (nom) => COULEURS_AVATAR[[...nom].reduce((t, c) => t + c.charCodeAt(0), 0) % COULEURS_AVATAR.length];

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const dateAvis = (date) => {
  const [annee, mois] = String(date || '').split('-');
  return annee && mois ? `${MOIS[Number(mois) - 1]} ${annee}` : '';
};
const noteFr = (note) => Number(note).toFixed(1).replace('.', ',');

const Etoiles = ({ nombre, taille = 22 }) => (
  <Box sx={{ display: 'inline-flex', gap: '2px' }} role="img" aria-label={`${nombre} étoiles sur 5`}>
    {Array.from({ length: nombre }).map((_, i) => <StarIcon key={i} sx={{ fontSize: taille, color: ETOILE }} />)}
  </Box>
);

const PastilleGoogle = ({ taille = 20 }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: taille, height: taille, borderRadius: '50%', backgroundColor: '#eef3fd', color: '#1a73e8',
      display: 'inline-grid', placeItems: 'center', fontWeight: 900, fontSize: taille * 0.55, flexShrink: 0,
    }}
  >
    G
  </Box>
);

const Initiale = ({ nom, taille = 72, anneau = FOND }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: taille, height: taille, borderRadius: '50%', backgroundColor: couleurAvatar(nom), color: '#fff',
      display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: taille * 0.38, flexShrink: 0,
      boxShadow: `0 0 0 6px ${anneau}, 0 10px 20px rgba(0,0,0,0.14)`,
    }}
  >
    {nom.charAt(0).toUpperCase()}
  </Box>
);

/**
 * Carte d'un avis : initiale qui dépasse du haut, nom, date, étoiles, texte.
 * `lignes` coupe le texte (bandeau) ; « Lire la suite » n'apparaît que si le
 * texte est réellement coupé.
 */
const CarteAvis = ({ avis, lignes, onLireLaSuite, cachee = false, sx }) => {
  const texteRef = useRef(null);
  const [coupe, setCoupe] = useState(false);

  useLayoutEffect(() => {
    const el = texteRef.current;
    if (!lignes || !el) return undefined;
    const mesurer = () => setCoupe(el.scrollHeight > el.clientHeight + 2);
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [lignes, avis.texte]);

  return (
    <Box
      component="article"
      aria-hidden={cachee || undefined}
      sx={{
        position: 'relative', backgroundColor: '#fff', borderRadius: '22px', border: '1px solid #ececf2',
        boxShadow: '0 10px 28px rgba(20,20,40,0.07)', px: 3.25, pt: 6.5, pb: 2.5,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center',
        transition: 'transform .25s ease, box-shadow .25s ease',
        '@media (hover: hover)': { '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 22px 44px rgba(20,20,40,0.12)' } },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        ...sx,
      }}
    >
      <Box sx={{ position: 'absolute', top: -36, left: '50%', transform: 'translateX(-50%)' }}>
        <Initiale nom={avis.nom} />
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute', top: 14, right: 16, width: 36, height: 36, borderRadius: '50%',
          backgroundColor: 'rgba(198,40,40,0.08)', color: ROUGE, display: 'grid', placeItems: 'center',
          fontWeight: 900, fontSize: '1.7rem', lineHeight: 1, pt: '12px',
        }}
      >
        “
      </Box>

      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.08rem', color: '#1a1a1a', overflowWrap: 'anywhere' }}>{avis.nom}</Typography>
        {avis.date_avis && <Typography sx={{ fontSize: '0.8rem', color: '#6b6b7b' }}>{dateAvis(avis.date_avis)}</Typography>}
      </Box>
      <Etoiles nombre={avis.etoiles} />
      <Typography
        ref={texteRef}
        component="blockquote"
        sx={{
          m: 0, fontSize: '1.02rem', lineHeight: 1.75, color: '#1a1a1a',
          ...(lignes ? { display: '-webkit-box', WebkitLineClamp: lignes, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
        }}
      >
        {avis.texte}
      </Typography>
      {lignes && coupe && (
        <Button
          size="small"
          onClick={() => onLireLaSuite(avis)}
          tabIndex={cachee ? -1 : 0}
          sx={{ color: ROUGE, fontWeight: 700, textTransform: 'none', p: 0, minWidth: 0 }}
        >
          Lire la suite
        </Button>
      )}
      {avis.source === 'google' && (
        <Box sx={{
          mt: 'auto', pt: 1.5, borderTop: '1px solid #f0f0f5', alignSelf: 'stretch',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, fontSize: '0.8rem', color: '#6b6b7b',
        }}
        >
          <PastilleGoogle /> Avis publié sur Google
        </Box>
      )}
    </Box>
  );
};

/** Bandeau défilant (ordinateur) : s'arrête au survol et pendant la lecture. */
const Bandeau = ({ avis, enPause, onLireLaSuite }) => {
  // Trop peu d'avis pour boucler : on les pose simplement côte à côte.
  if (avis.length < 3) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', pt: 6 }}>
        {avis.map((a) => <CarteAvis key={a.id} avis={a} lignes={4} onLireLaSuite={onLireLaSuite} sx={{ width: 340 }} />)}
      </Box>
    );
  }

  // Le contenu est répété une fois : à la moitié du trajet, la seconde copie
  // est exactement à la place de la première, et la boucle ne se voit pas.
  const duree = Math.max(40, avis.length * 12);
  return (
    <Box
      aria-label="Avis des apprenants, défilement automatique qui s'arrête au survol"
      sx={{
        overflow: 'hidden',
        WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
        maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
        '&:hover .rail-avis, &:focus-within .rail-avis': { animationPlayState: 'paused' },
        '@media (prefers-reduced-motion: reduce)': { overflowX: 'auto' },
      }}
    >
      <Box
        className="rail-avis"
        sx={{
          display: 'flex', alignItems: 'stretch', gap: '22px', width: 'max-content', pt: 6, pb: 2.5,
          animation: `avisDefilement ${duree}s linear infinite`,
          animationPlayState: enPause ? 'paused' : 'running',
          '@keyframes avisDefilement': { to: { transform: 'translateX(calc(-50% - 11px))' } },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {[...avis, ...avis].map((a, i) => (
          <CarteAvis
            key={`${a.id}-${i}`}
            avis={a}
            lignes={4}
            cachee={i >= avis.length}
            onLireLaSuite={onLireLaSuite}
            sx={{ width: 330, flexShrink: 0 }}
          />
        ))}
      </Box>
    </Box>
  );
};

/** Un avis à la fois (mobile) : tout seul, avec les flèches ou au doigt. */
const UnAvis = ({ avis }) => {
  const [index, setIndex] = useState(0);
  const derniereAction = useRef(0);
  const departX = useRef(null);

  const aller = useCallback((nouvel) => {
    derniereAction.current = Date.now();
    setIndex((nouvel + avis.length) % avis.length);
  }, [avis.length]);

  useEffect(() => {
    if (avis.length <= 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const minuterie = setInterval(() => {
      if (Date.now() - derniereAction.current < PAUSE_APRES_ACTION) return;
      setIndex((i) => (i + 1) % avis.length);
    }, DELAI_MOBILE);
    return () => clearInterval(minuterie);
  }, [avis.length]);

  const avisCourant = avis[Math.min(index, avis.length - 1)];
  const bouton = {
    width: 50, height: 50, backgroundColor: ROUGE, color: '#fff', boxShadow: '0 6px 16px rgba(198,40,40,0.3)',
    '&:hover': { backgroundColor: '#a31f1f' },
  };

  return (
    <Box sx={{ maxWidth: 440, mx: 'auto', display: 'grid', gap: 2.25 }}>
      <Box
        aria-live="polite"
        sx={{ pt: 5, touchAction: 'pan-y' }}
        onTouchStart={(e) => { departX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (departX.current === null) return;
          const ecart = e.changedTouches[0].clientX - departX.current;
          departX.current = null;
          if (Math.abs(ecart) >= 40) aller(index + (ecart < 0 ? 1 : -1));
        }}
      >
        <CarteAvis
          key={avisCourant.id}
          avis={avisCourant}
          sx={{
            animation: 'avisFondu .4s ease',
            '@keyframes avisFondu': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </Box>

      {avis.length > 1 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <IconButton onClick={() => aller(index - 1)} aria-label="Avis précédent" sx={bouton}>
              <ChevronLeftIcon sx={{ fontSize: 30 }} />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {avis.map((a, i) => (
                <Box
                  key={a.id}
                  component="button"
                  type="button"
                  onClick={() => aller(i)}
                  aria-label={`Avis de ${a.nom}`}
                  aria-current={i === index}
                  sx={{
                    p: 0, border: 'none', cursor: 'pointer', height: 12, borderRadius: '999px',
                    width: i === index ? 30 : 12, backgroundColor: i === index ? ROUGE : '#cfd1dc',
                    transition: 'width .25s ease, background-color .25s ease',
                    '&:focus-visible': { outline: `2px solid ${ROUGE}`, outlineOffset: 3 },
                  }}
                />
              ))}
            </Box>
            <IconButton onClick={() => aller(index + 1)} aria-label="Avis suivant" sx={bouton}>
              <ChevronRightIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Box>
          <Typography sx={{ textAlign: 'center', fontSize: '0.8rem', color: '#6b6b7b', mt: -1 }}>
            {index + 1} / {avis.length}
          </Typography>
        </>
      )}
    </Box>
  );
};

/**
 * Section « Ce que disent nos apprenants » de la page d'accueil.
 * Les avis et les réglages viennent de l'API (page « Avis » de
 * l'administration) ; les avis d'origine servent de secours.
 */
const AvisSection = ({ label, titre }) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [donnees, setDonnees] = useState(SECOURS);
  const [avisOuvert, setAvisOuvert] = useState(null);

  useEffect(() => {
    let actif = true;
    axios.get(`${API_URL}/avis`)
      .then(({ data }) => { if (actif && data?.success) setDonnees(data.data); })
      .catch(() => { /* on garde les avis de secours */ });
    return () => { actif = false; };
  }, []);

  const { avis, reglages } = donnees;
  if (!avis?.length) return null;

  const afficherNote = reglages?.note != null && reglages?.nombre_avis != null;

  return (
    <>
      {label}
      {titre}

      {afficherNote && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2, mb: { xs: 1, md: 0 } }}>
          <Box
            component={reglages.lien_google ? 'a' : 'div'}
            href={reglages.lien_google || undefined}
            target={reglages.lien_google ? '_blank' : undefined}
            rel={reglages.lien_google ? 'noopener noreferrer' : undefined}
            aria-label={`Note ${noteFr(reglages.note)} sur 5, ${reglages.nombre_avis} avis Google`}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', borderRadius: '999px',
              pl: 1, pr: 2.5, py: 1, border: '1px solid #ececf2', boxShadow: '0 6px 18px rgba(20,20,40,0.06)',
              textDecoration: 'none', color: 'inherit', flexWrap: 'wrap', justifyContent: 'center',
            }}
          >
            <PastilleGoogle taille={38} />
            <Typography component="span" sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 900, lineHeight: 1, color: '#1a1a1a' }}>
              {noteFr(reglages.note)}
            </Typography>
            <Etoiles nombre={Math.round(reglages.note)} taille={mobile ? 17 : 22} />
            <Typography component="span" sx={{ color: '#3f3f4d', fontWeight: 500 }}>
              {reglages.nombre_avis} avis Google
            </Typography>
          </Box>
        </Box>
      )}

      {mobile
        ? <UnAvis avis={avis} />
        : <Bandeau avis={avis} enPause={Boolean(avisOuvert)} onLireLaSuite={setAvisOuvert} />}

      {!mobile && avis.length >= 3 && (
        <Typography sx={{ textAlign: 'center', fontSize: '0.82rem', color: '#6b6b7b', mt: -0.5 }}>
          Le bandeau s'arrête quand la souris passe dessus.
        </Typography>
      )}

      {/* Invitation à contribuer + accès à l'ensemble des avis. */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography sx={{ color: '#5b5b6b', mb: 2 }}>
          Vous avez suivi une formation chez nous ? Votre avis aide les prochains apprenants à se décider.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          {reglages?.lien_avis && (
            <Button
              variant="contained"
              href={reglages.lien_avis}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<StarIcon />}
              sx={{ backgroundColor: ROUGE, fontWeight: 700, px: 3, '&:hover': { backgroundColor: '#a31f1f' } }}
            >
              Laisser un avis
            </Button>
          )}
          {reglages?.lien_google && (
            <Button
              variant="outlined"
              href={reglages.lien_google}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderColor: '#d8d8e2', color: '#3f3f4d', fontWeight: 600, px: 3, '&:hover': { borderColor: ROUGE, color: ROUGE } }}
            >
              Voir tous les avis
            </Button>
          )}
        </Stack>
      </Box>

      {/* Avis complet, ouvert par « Lire la suite » ; le bandeau reste en pause. */}
      <Dialog
        open={Boolean(avisOuvert)}
        onClose={() => setAvisOuvert(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '22px' } } }}
        aria-label={avisOuvert ? `Avis de ${avisOuvert.nom}` : undefined}
      >
        {avisOuvert && (
          <DialogContent sx={{ position: 'relative', pt: 4, pb: 3, display: 'grid', gap: 1.75, justifyItems: 'center', textAlign: 'center' }}>
            <IconButton onClick={() => setAvisOuvert(null)} aria-label="Fermer" sx={{ position: 'absolute', top: 12, right: 12, backgroundColor: '#f1f2f7' }}>
              <CloseIcon />
            </IconButton>
            <Initiale nom={avisOuvert.nom} taille={64} anneau="#fff" />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{avisOuvert.nom}</Typography>
              {avisOuvert.date_avis && <Typography sx={{ fontSize: '0.82rem', color: '#6b6b7b' }}>{dateAvis(avisOuvert.date_avis)}</Typography>}
            </Box>
            <Etoiles nombre={avisOuvert.etoiles} />
            <Typography component="blockquote" sx={{ m: 0, fontSize: '1.08rem', lineHeight: 1.8, color: '#1a1a1a' }}>
              {avisOuvert.texte}
            </Typography>
            {avisOuvert.source === 'google' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8rem', color: '#6b6b7b' }}>
                <PastilleGoogle /> Avis publié sur Google
              </Box>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default AvisSection;
